import React, { useState, useContext } from "react";
import { Button, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";

import { DateFormat } from "../../../helpers";
import Sidebar from "../../../components/Sidebar";
import PreviewBackdrop from "../../../components/PreviewBackdrop";
import { StoreContext } from "../../../context";
import "./index.css";

const { Text } = Typography;

export default function BackdropSidebar({
  visible = false,
  data = {},
  onClose,
  editBackdropHandler,
  onUploadDone,
}) {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);

  const renderInfoSection = (title, children) => (
    <div className="info-section">
      <Text className="section-title">{title}</Text>
      {children}
    </div>
  );

  const renderDetailItem = (label, value, isColor = false) => {
    if (!value && !isColor) return null;

    return (
      <div className="detail-item">
        <Text className="detail-label">{label}</Text>
        {isColor ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 4,
                backgroundColor: value,
                border: "1px solid #d9d9d9",
              }}
            ></div>
            <Text className="detail-value">{value}</Text>
          </div>
        ) : (
          <Text className="detail-value">{value}</Text>
        )}
      </div>
    );
  };

  return (
    <Sidebar
      title="Backdrop Details"
      visible={visible}
      onClose={onClose}
      loading={loading}
      footer={
        !storeId && (
          <div
            style={{
              display: "flex",
              gap: "12px",
              padding: "16px 0",
            }}
          >
            <Button
              icon={<EditOutlined />}
              onClick={editBackdropHandler}
              type="primary"
              size="large"
              style={{
                width: "100%",
                height: "48px",
                borderRadius: "8px",
              }}
            >
              Edit Backdrop Details
            </Button>
          </div>
        )
      }
    >
      <div style={{ padding: "0 4px" }}>
        {/* Backdrop Preview */}
        {data.poster_link && (
          <div style={{ marginBottom: "20px" }}>
            <Text className="section-title">Backdrop Preview</Text>
            <PreviewBackdrop
              backdropSrc={data.poster_link}
              bannerSrc={data.banner_image_url || null}
              bannerData={data.banner_image_url ? { banner_image: data.banner_image_url, banner_id: data.banner_id } : null}
              colour1={data.colour_1 || "#FFF0F2"}
              colour2={data.colour_2 || "#FF4C64"}
              iconColour={data.icon_colour || "#000000"}
              contentType={data.content_type || "image/png"}
            />
          </div>
        )}

        {/* Basic Information */}
        {renderInfoSection(
          "Basic Information",
          <>
            {renderDetailItem("Backdrop Name", data.name)}
            {renderDetailItem("Content Type", data.content_type)}
            {renderDetailItem("Created Date", DateFormat(data.created_at))}
            <div className="detail-item">
              <Text className="detail-label">Status</Text>
              <span
                className="banner-type-badge"
                style={{
                  backgroundColor: data.is_active === 1 ? "#52c41a" : "#ff4d4f",
                }}
              >
                {data.is_active === 1 ? "Active" : "Inactive"}
              </span>
            </div>
          </>
        )}

        {/* Color Information */}
        {renderInfoSection(
          "Color Settings",
          <>
            {renderDetailItem("Colour 1 (Top Gradient)", data.colour_1, true)}
            {renderDetailItem("Colour 2 (Bottom Gradient)", data.colour_2, true)}
            {renderDetailItem("Icon Colour", data.icon_colour, true)}

            {/* Gradient Preview */}
            <div className="detail-item">
              <Text className="detail-label">Gradient Preview</Text>
              <div
                style={{
                  width: "100%",
                  height: 60,
                  borderRadius: 8,
                  background: `linear-gradient(180deg, ${data.colour_1} 0%, ${data.colour_2} 100%)`,
                  border: "1px solid #d9d9d9",
                }}
              ></div>
            </div>
          </>
        )}

        {/* Banner Information */}
        {data.banner_name && renderInfoSection(
          "Associated Banner",
          <>
            {renderDetailItem("Banner Name", data.banner_name)}
            {data.banner_image_url && (
              <div className="detail-item">
                <Text className="detail-label">Banner Preview</Text>
                <img
                  src={data.banner_image_url}
                  alt="Banner"
                  style={{
                    width: "100%",
                    borderRadius: 8,
                    marginTop: 8,
                    border: "1px solid #d9d9d9",
                  }}
                />
              </div>
            )}
          </>
        )}

        {/* Validity Period */}
        {renderInfoSection(
          "Validity Period",
          <>
            {renderDetailItem(
              "Start Date",
              DateFormat(data.start_date, "DD MMM YY h:mm A")
            )}
            {renderDetailItem(
              "End Date",
              DateFormat(data.end_date, "DD MMM YY h:mm A")
            )}

            <div className="detail-item">
              <Text className="detail-label">Duration</Text>
              <Text className="detail-value">
                {DateFormat(data.start_date, "DD MMM YY")} -{" "}
                {DateFormat(data.end_date, "DD MMM YY")}
              </Text>
            </div>
          </>
        )}
      </div>
    </Sidebar>
  );
}
